
import { Route } from "vue-router";
import store from "./store/";
import { ExceedThresholdResult } from "./store/types";

// Track number of route changes
let routeChangeCount: number = 0;

const checkRouteChangeOverLimit = (): ExceedThresholdResult => {
	const eventQueueRefreshMaxRouteChangesDefault: number = 50;
	const maxRouteChangesAllowed: number = store.state.tenantSystemPrefs.eventQueueRefreshMaxRouteChanges ??
		eventQueueRefreshMaxRouteChangesDefault;

	return {
		exceeded: routeChangeCount > maxRouteChangesAllowed,
		actual: routeChangeCount ?? 0,
		threshold: maxRouteChangesAllowed ?? 0
	};
}

const checkPerformanceOverLimit = (): ExceedThresholdResult => {

	if (window.performance && (window.performance as any).memory) {
		const eventQueueRefreshThresholdMBDefault: number = 1024;
		const memoryThreshold: number = store.state.tenantSystemPrefs.eventQueueRefreshThresholdMB ??
			eventQueueRefreshThresholdMBDefault;

		const usedMemoryBytes: number = (window.performance as any).memory.usedJSHeapSize;
		const MbAsBytes: number = 1048576;

		// Convert Bytes to MB
		const usedMemoryMB = usedMemoryBytes / MbAsBytes;

		return {
			exceeded: usedMemoryMB > memoryThreshold,
			actual: usedMemoryMB ?? 0,
			threshold: memoryThreshold ?? 0
		};
	}

	return {
			exceeded: false,
			actual: null,
			threshold: null
		};
}

/**
 * Checks if a reload is required on the EventQueue based on the amount of memory used or the total number of route changes.
 * Requires the MemoryManagement/EventQueueForceRefresh feature enabled and navigation to the event-queue page for check to occur.
 * @param {Route} to - The to route
 * @param {Route} from - The from route
 */
export const checkIfEventQueueReloadRequired = (to: Route, from: Route): boolean => {
	const hasFeature: boolean = store.getters["getFeature"](["MemoryManagement", "EventQueueForceRefresh"]);

	if (hasFeature) {
		routeChangeCount++;
        const routeNameToCheck: string = "event-queue";
        const isTargetRoute: boolean = to.name === routeNameToCheck;

        // No need to do anything when not on target route
        if (!isTargetRoute){
            return false;
        }

		// if the "to" route path is the same
        if (from?.name && from.name === routeNameToCheck) {
            return false;
        }

		const isOverRouteLimit: ExceedThresholdResult = checkRouteChangeOverLimit();
		const isOverMemoryLimit: ExceedThresholdResult = checkPerformanceOverLimit();

		// Only reload if a limit is hit
		const reloadRequired: boolean = isOverRouteLimit?.exceeded || isOverMemoryLimit?.exceeded;

		if (reloadRequired) {

			const performanceSupported: boolean = window.performance && (window.performance as any).memory;

			let logMessage: string = `Maintenance reload: [Route Count exceeded: ${isOverRouteLimit?.exceeded} (${isOverRouteLimit.actual}/${isOverRouteLimit.threshold})`;

			if (performanceSupported && isOverMemoryLimit.threshold)
				logMessage += `, Memory Limit Exceeded: ${isOverMemoryLimit.exceeded} (${isOverMemoryLimit.actual.toFixed(2)}MB/${isOverMemoryLimit.threshold}MB)`;

			logMessage += "]";

			console.log(logMessage);

			return true;
		}
	}

	return false;
}